import React, { useCallback, useContext, useMemo, useState } from "react";
import { Button, Grid } from "@mui/material";
import { FaSave } from "react-icons/fa";
import PopupModal from "../../PopupModal";
import DropdownField from "../../core/DropdownField";
import { DFORM_ASSIGNED_FOR, EXPERTS_POSITIONS, MOVEMENTS_STATUS, REPORT_ASSIGNMENT_STATUS } from "modules/pvs_activities/constants";
import { useSelector } from "react-redux";
import { FormDataContext } from "../../contexts/FormContext";
import { displayNotification } from "helpers";
import { useSubmitDFormMutation } from "modules/pvs_activities/redux/service/dForm";
import { useNavigate } from "react-router-dom";
import { useGetFormDataVariablesQuery } from "modules/pvs_activities/redux/service/formData";
import { processVariables } from "modules/pvs_activities/helper.general";
import { USER_ROLES } from "constants";
import { useLazyGetMissionExpertLatestInfoQuery } from "modules/pvs_activities/redux/service/missionExpert";

const SubmitReportModal = ({
    className = "",
    templateTypeName = "Form",
    subStatus = "",
    subStatusName = "-",
    templateId = "",
    movementId = "",
    isPopupForm = false,
    onSubmit = () => { },
}) => {
    //

    const navigate = useNavigate();
    const authData = useSelector((s) => s.auth.data);
    const formDataContext = useContext(FormDataContext);
    
    const variablesRes = useGetFormDataVariablesQuery(formDataContext.missionId, {
        skip: formDataContext.mode !== "data_entry"
    });
    const [missionExpertInfoTrigger, missionExpertInfoRes] = useLazyGetMissionExpertLatestInfoQuery();
    const [submitDForm] = useSubmitDFormMutation();
    const [action, setAction] = useState("");

    const actionOptions = useMemo(() => {
        const opts = [];
        if (!missionExpertInfoRes.isSuccess && !missionExpertInfoRes.isUninitialized) return opts;
        if (REPORT_ASSIGNMENT_STATUS.IN_DRAFT === parseInt(subStatus)) {
            opts.push({ label: "Submit to Mission Manager", value: "CASE_0" });
            opts.push({ label: "Submit to Peer Reviewer", value: "CASE_1" });
        }
        if (REPORT_ASSIGNMENT_STATUS.IN_PEER_REVIEW === parseInt(subStatus)) {
            opts.push({ label: "Submit comments to Expert Team", value: "CASE_2" });
            opts.push({ label: "Submit for WOAH Reviewer", value: "CASE_1" });
        }
        if (REPORT_ASSIGNMENT_STATUS.UNDER_WOAH_REVIEW === parseInt(subStatus)) {
            opts.push({ label: "Submit to Team Leader", value: "CASE_2" });
            opts.push({ label: "Submit to Peer Reviewer", value: "CASE_4" });
            opts.push({ label: "Submit to Delegate", value: "CASE_1" });
        }
        if (REPORT_ASSIGNMENT_STATUS.UNDER_DELEGATE_REVIEW === parseInt(subStatus)) {
            opts.push({ label: "Submit comments to Mission Manager", value: "CASE_5" });
            opts.push({ label: "Mark Report as Accepted", value: "CASE_6" });
        }
        if (REPORT_ASSIGNMENT_STATUS.COMMENT_RESOLUTION === parseInt(subStatus)) {
            if (USER_ROLES.business_admin.includes(parseInt(authData.role_id))) {
                opts.push({ label: "Submit to Team Leader", value: "CASE_7:1" });
                opts.push({ label: "Submit to Peer Reviewer", value: "CASE_7:2" });
                opts.push({ label: "Submit to Delegate", value: "CASE_7:3" });
            } else if (USER_ROLES.delegate.includes(parseInt(authData.role_id))) {
                opts.push({ label: "Submit comments to Mission Manager", value: "CASE_7:4" });
                opts.push({ label: "Mark Report as Accepted", value: "CASE_6" });
            } else if (USER_ROLES.experts.includes(parseInt(authData.role_id))) {
                if (!missionExpertInfoRes.isSuccess || missionExpertInfoRes.data === null) return opts;
                if (EXPERTS_POSITIONS.team_leader === missionExpertInfoRes.data.position_id) {
                    opts.push({ label: "Submit to Mission Manager", value: "CASE_7:4" });
                    opts.push({ label: "Submit to Peer Reviewer", value: "CASE_7:2" });
                } else if (EXPERTS_POSITIONS.peer_reviewer === missionExpertInfoRes.data.position_id) {
                    opts.push({ label: "Submit comments to Expert Team", value: "CASE_7:1" });
                    opts.push({ label: "Submit for WOAH Reviewer", value: "CASE_7:4" });
                }
            }
        }
        return opts;
    }, [subStatus, authData, missionExpertInfoRes]);

    const handleSubmitAction = useCallback(
        (onClose) => {
            const [baseAction, subAction = 0] = String(action).split(':');
            switch (baseAction) {
                case "CASE_0":
                    handleSubmitClick(parseInt(subStatus) === DFORM_ASSIGNED_FOR.approval, null, null, false);
                    onClose();
                    break;
                case "CASE_1":
                    handleSubmitClick(false, null, null, true);
                    onClose();
                    break;
                case "CASE_2": //
                    handleSubmitClick(false, null, REPORT_ASSIGNMENT_STATUS.IN_DRAFT, true);
                    onClose();
                    break;
                case "CASE_3":
                    handleSubmitClick(false, null, REPORT_ASSIGNMENT_STATUS.UNDER_DELEGATE_REVIEW, true);
                    onClose();
                    break;
                case "CASE_4": //
                    handleSubmitClick(false, null, REPORT_ASSIGNMENT_STATUS.IN_PEER_REVIEW, true);
                    onClose();
                    break;
                case "CASE_5":
                    handleSubmitClick(false, null, REPORT_ASSIGNMENT_STATUS.COMMENT_RESOLUTION, true);
                    onClose();
                    break;
                case "CASE_6":
                    handleSubmitClick(false, MOVEMENTS_STATUS.completed, REPORT_ASSIGNMENT_STATUS.COMPLETED, true);
                    onClose();
                    break;
                case "CASE_7":
                    let userAction = null;
                    if (subAction === "1") userAction = "TEAM_LEADER";
                    else if (subAction === "2") userAction = "PEER_REVIEWER";
                    else if (subAction === "3") userAction = "DELEGATE";
                    else if (subAction === "4") userAction = "MISSION_MANAGER";
                    else {
                        displayNotification("warning", "Invalid Action/Sub-Action for Report Submit");
                        break;
                    }
                    handleSubmitClick(false, null, REPORT_ASSIGNMENT_STATUS.COMMENT_RESOLUTION, true, userAction);
                    onClose();
                    break;
                default:
                    displayNotification("warning", "Invalid Action for Report Submit");
            }
        },
        [action, subStatus]
    );

    const handleSubmitClick = (isValidated, submission_for = null, dform_assigned_for = null, trigger_assignment = null, userAction = null) => {
        const check = localStorage.getItem(`pvsa-process-return-url`);
        const submitCallback = () => {
            if (movementId && window.confirm("You are about to submit. Are you sure?")) {
                let typ = "";
                const userId = authData.id;
                if (formDataContext.formType === "FT_RPT") {
                    typ = "report";
                }
                submitDForm({
                    templateId,
                    movementId,
                    typ,
                    isValidated,
                    userId,
                    ...(submission_for ? { submission_for } : {}),
                    ...(dform_assigned_for ? { dform_assigned_for } : {}),
                    ...(trigger_assignment ? { trigger_assignment } : {}),
                    ...(userAction ? { userAction } : {}),
                }).then((response) => {
                    if (response.error) {
                        displayNotification("error", response.error.data.message);
                    } else {
                        displayNotification("success", response.data.message);
                        if (isPopupForm === false) {
                            navigate(check);
                            localStorage.removeItem(`pvsa-process-return-url`);
                        }
                    }
                });
            }
        };
        if (isPopupForm) onSubmit(submitCallback);
        else if (check !== null) submitCallback();
    };

    return (
        <PopupModal
            title={`Submit ${templateTypeName}`}
            triggers={({openModal}) => (
                <Button variant="contained" color="primary" onClick={openModal} className={className}>
                    <FaSave className="me-1" />
                    <span>Ready to Submit?</span>
                </Button>
            )}
            footTriggers={({closeModal}) => (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmitAction(closeModal)}
                    disabled={action === ""}
                >
                    <FaSave className="me-1" />
                    <span>Submit</span>
                </Button>
            )}
            onOpen={() => {
                if (USER_ROLES.experts.includes(parseInt(authData.role_id))) {
                    missionExpertInfoTrigger({ userId: authData.id, missionId: formDataContext.missionId });
                }
            }}
            onClose={() => setAction("")}
        >
            <>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <strong>Current Status:</strong>
                        <p>{subStatusName}</p>
                    </Grid>
                    <Grid item xs={6}>
                        <strong>Country:</strong>
                        <p>{processVariables(variablesRes, "{country}")}</p>
                    </Grid>
                </Grid>
                <DropdownField
                    plain={true}
                    label={"Select Action"}
                    required={true}
                    hasOptions={true}
                    options={actionOptions}
                    value={action}
                    onChange={setAction}
                />
            </>
        </PopupModal>
    );
};

export default SubmitReportModal;
